import type { AxiosError, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import axios from "axios";
import { v4 } from "uuid";
import { auth, config as configuration } from "../config";
import { setUnAuthTokenAndTimer } from "../services/UnAuthToken.service";
import type { ApiResponse } from "@/types";

declare global {
  interface Window {
    mobileAppToken?: string;
    isMobileApp?: boolean;
  }
}

// Set axios defaults to not send cookies automatically
// This prevents Okta and other cookies from being sent with API requests
axios.defaults.withCredentials = false;

// Configure axios instances to not send cookies automatically
// This prevents Okta and other cookies from being sent with API requests
export const instance = axios.create({
  withCredentials: false,
});
export const selfServInstance = axios.create({
  withCredentials: false,
});
// Store injection for potential future use
// eslint-disable-next-line @typescript-eslint/no-unused-vars
let _store: unknown;

export const injectStore = (_storeParam: unknown) => {
  _store = _storeParam;
};

// Helper function to get authentication token
// This function is synchronous and doesn't refresh tokens
// Token refresh is handled in the request interceptor
function getAuthToken(): string | null {
  // Check for mobile app token first
  const mobileToken =
    localStorage.getItem("mobileAppToken") ?? window.mobileAppToken;
  const isMobile =
    localStorage.getItem("device") === "mobile" || window.isMobileApp;

  if (isMobile && mobileToken) {
    console.log("Using mobile app token for authentication");
    return mobileToken;
  }

  // For unauthenticated API calls, prioritize unAuthToken over Okta token
  // This ensures unauthenticated endpoints get the correct token
  const unAuthToken = configuration.env("unAuthToken");
  if (unAuthToken) {
    // Verify token is not expired before using it
    const tokenExpiration = configuration.env("timer");
    if (tokenExpiration) {
      const currentTime = Math.floor(Date.now() / 1000);
      const expirationTime = parseInt(tokenExpiration, 10);
      
      // Don't use expired tokens - return null so interceptor can refresh
      if (expirationTime <= currentTime) {
        console.warn(
          "[Token] Token has expired, interceptor should refresh it",
        );
        return null;
      }
    }
    
    console.log("Using unauthenticated token for API call");
    return unAuthToken;
  }

  // Fall back to Okta token for authenticated web requests
  if (!auth.isNotDefined) {
    const oktaToken = auth.getOkta().getAccessToken();
    if (oktaToken) {
      console.log("Using Okta token for authentication");
      return oktaToken;
    }
  }

  console.warn(
    "No authentication token available. unAuthToken may not have been loaded at app startup.",
  );
  return null;
}

export async function axiosApi(
  config: AxiosRequestConfig,
): Promise<ApiResponse> {
  return instance(config)
    .then((response: AxiosResponse) => ({
      response: response.data.responseDetails ?? response.data,
      error: {
        id: "",
        message: "",
        status: 0,
      },
    }))
    .catch((error: AxiosError) => {
      const message = error.message ?? "Unknown error";
      const status = error.response?.status ?? 0;
      const data = error.response?.data;
      return {
        response: undefined,
        error: { id: v4(), message, status, data },
      };
    });
}

/**
 * Check if device is mobile
 */
function isMobileDevice(): boolean {
  return (
    localStorage.getItem("device") === "mobile" || window.isMobileApp === true
  );
}

/**
 * Check if mobile token exists
 */
function hasMobileToken(): boolean {
  return (
    localStorage.getItem("mobileAppToken") !== null ||
    window.mobileAppToken !== undefined
  );
}

/**
 * Determine if token should be refreshed based on expiration
 */
function shouldRefreshToken(tokenExpiration: string | null): boolean {
  if (!tokenExpiration) {
    console.log("[Token Refresh] No token expiration found, fetching initial token...");
    return true;
  }

  const currentTime = Math.floor(Date.now() / 1000);
  const expirationTime = parseInt(tokenExpiration, 10);
  const timeUntilExpiration = expirationTime - currentTime;

  if (timeUntilExpiration < 180) {
    if (timeUntilExpiration <= 0) {
      console.log("[Token Refresh] Token has expired, refreshing immediately...");
    } else {
      console.log(
        `[Token Refresh] Token expires in ${timeUntilExpiration} seconds, refreshing proactively...`,
      );
    }
    return true;
  }

  return false;
}

/**
 * Get xApiKey from various sources
 */
function getXApiKey(): string | undefined {
  if (typeof window !== "undefined") {
    const runtimeConfig = (
      window as Window & { __authShellConfig?: { xApiKey?: string } }
    ).__authShellConfig;
    if (runtimeConfig?.xApiKey) {
      return runtimeConfig.xApiKey;
    }
  }
  return configuration.env("xApiKey") ?? undefined;
}

/**
 * Refresh token if needed
 */
async function refreshTokenIfNeeded(): Promise<void> {
  if (isMobileDevice() || hasMobileToken()) {
    return;
  }

  const configDefined = configuration;
  if (!configDefined) {
    return;
  }

  try {
    const tokenExpiration = configDefined.env("timer");
    if (!shouldRefreshToken(tokenExpiration)) {
      return;
    }

    const baseUrl = configDefined.env("baseUrl");
    const xApiKey = getXApiKey();

    if (!baseUrl || !xApiKey) {
      console.warn(
        "[Token Refresh] Cannot refresh token: missing baseUrl or xApiKey",
        { baseUrl: !!baseUrl, xApiKey: !!xApiKey },
      );
      return;
    }

    try {
      await setUnAuthTokenAndTimer(baseUrl, xApiKey);
      console.log("[Token Refresh] Token refreshed successfully");
    } catch (refreshError) {
      console.error("[Token Refresh] Failed to refresh token:", refreshError);
    }
  } catch (error) {
    console.error("[Token Refresh] Error during token refresh:", error);
  }
}

/**
 * Handle token endpoint configuration
 */
function handleTokenEndpoint(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  const correlationId = v4();
  config.withCredentials = false;
  if (!config.headers) {
    config.headers = {} as typeof config.headers;
  }
  config.headers["x-correlation-id"] = correlationId;
  config.headers.startTime = new Date().getTime();
  return config;
}

/**
 * Get token with retry logic
 */
function getTokenWithRetry(): string | null {
  let token = getAuthToken();
  if (!token && !isMobileDevice() && !hasMobileToken()) {
    token = getAuthToken();
  }
  return token;
}

/**
 * Build request headers based on URL and token
 */
function buildRequestHeaders(
  config: InternalAxiosRequestConfig,
  token: string | null,
): void {
  const reqUrl = config.url ?? "";
  const cleanUrl = reqUrl.indexOf("?") > 0
    ? reqUrl.substring(0, reqUrl.indexOf("?"))
    : reqUrl;

  if (!config.headers) {
    config.headers = {} as typeof config.headers;
  }

  config.headers.startTime = new Date().getTime();

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    console.log("Authorization header added to request:", cleanUrl);
  } else {
    console.warn(
      "No token available for request:",
      cleanUrl,
      "- Request may fail with authentication error",
    );
  }

  if (config.url?.includes("gateway")) {
    config.headers["x-api-key"] = `${configuration.env("xApiKey")}`;
  } else {
    config.headers.channel = "servicing";
    config.headers.division = "digital_originations";
    config.headers.application = "auth_customer_portal";
    config.headers.siteId = "02";
    config.headers["request-startTime"] = new Date().getTime();
  }
}

// Add a request interceptor
instance.interceptors.request.use(
  async function (config: InternalAxiosRequestConfig) {
    config.withCredentials = false;

    const isTokenEndpoint = config.url?.includes("/unauth/customers/token");

    if (isTokenEndpoint) {
      return handleTokenEndpoint(config);
    }

    await refreshTokenIfNeeded();

    const token = getTokenWithRetry();
    const correlationId = v4();
    buildRequestHeaders(config, token);
    config.headers.set("x-correlation-id", correlationId);
    config.withCredentials = false;

    return config;
  },
  function (error) {
    return Promise.reject(error instanceof Error ? error : new Error(String(error)));
  },
);

interface TError {
  errorDetails: Array<{ code: string; message: string; reason: string }>;
  success: boolean;
}

// Add a response interceptor
instance.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  function (error: AxiosError<TError>) {
    console.log("interceptor error response");
    return Promise.reject(error instanceof Error ? error : new Error(String(error)));
  },
);
